import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { destinoTrasEntrar } from '@/lib/destinoDeEntrada';
import { Button } from '@/components/ui/button';
import CavernicolAppLogo from '@/components/brand/CavernicolAppLogo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, User, ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import { useLocale, LanguageSelect } from '@/contexts/LocaleContext';

export default function Login() {
  const { t } = useLocale();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaChallengeToken, setMfaChallengeToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [marcaHost, setMarcaHost] = useState(null);
  const turnstileRef = useRef(null);
  const { login, verifyMfaLogin, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A dónde iba antes de que le pidiéramos identificarse. Lo pone el guardia
  // de rutas; `destinoTrasEntrar` lo sanea y, si no es de fiar, lo ignora.
  const volverA = searchParams.get('volver');

  useEffect(() => {
    if (searchParams.get('reason') === 'inactive') {
      setError(t('login.inactive'));
    }
  }, [searchParams]);

  useEffect(() => {
    api.get('/public/marca').then(({ data }) => {
      setMarcaHost(data);
      if (data?.brand_name) document.title = data.brand_name + ' — entrar';
      const href = data?.favicon_url || data?.logo_url;
      if (href) {
        const url = href + (href.includes('?') ? '&' : '?') + 'v=' + Date.now();
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = url;
      }
    }).catch(() => {});
  }, []);

  const resetCaptcha = () => {
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (isRegister) {
        const data = await register(email, password, name, turnstileToken);
        navigate(destinoTrasEntrar(data.user, volverA));
      } else {
        const data = await login(email, password, turnstileToken);
        if (data.mfa_required) {
          setMfaChallengeToken(data.challenge_token);
        } else navigate(destinoTrasEntrar(data.user, volverA));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      resetCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = await verifyMfaLogin(mfaChallengeToken, mfaCode);
      navigate(destinoTrasEntrar(data.user, volverA));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-5 py-8 sm:p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Product brand */}
          <div className="mb-10 flex items-start justify-between gap-3">
            <Link to="/" aria-label={marcaHost?.brand_name || 'Inicio'}>
              {marcaHost?.es_de_socio ? (
                marcaHost.logo_url ? (
                  <img src={marcaHost.logo_url} alt={marcaHost.brand_name}
                       className="max-h-10 max-w-[200px] object-contain" />
                ) : (
                  <span className="text-2xl font-semibold tracking-tight"
                        style={{ color: marcaHost.primary_color || '#0F172A' }}>
                    {marcaHost.brand_name}
                  </span>
                )
              ) : (
                <CavernicolAppLogo />
              )}
            </Link>
            <LanguageSelect />
          </div>

          {mfaChallengeToken ? (
            <>
              <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-1 flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-[#F72585]" /> {t('login.mfa')}
              </h2>
              <p className="text-sm text-[#64748B] mb-8">
                {t('login.mfaHint')}
              </p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="mfa-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleMfaSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="mfa-code" className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">
                    Código
                  </Label>
                  <Input
                    id="mfa-code"
                    data-testid="mfa-code-input"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder="123456"
                    autoFocus
                    className="h-11 bg-white border-[#E2E8F0] rounded-lg text-sm text-center tracking-[0.3em] focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="mfa-submit-button"
                  disabled={submitting}
                  className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] rounded-lg font-medium text-sm transition-all"
                >
                  {submitting ? 'Verificando...' : 'Verificar'}
                </Button>
                <button
                  type="button"
                  data-testid="mfa-back-button"
                  onClick={() => { setMfaChallengeToken(''); setMfaCode(''); setError(''); }}
                  className="w-full text-sm text-[#64748B] hover:text-[#0F172A] transition-colors"
                >
                  Volver
                </button>
              </form>
            </>
          ) : (
          <>
          <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-1">
            {isRegister ? 'Crear cuenta' : 'Iniciar sesión'}
          </h2>
          <p className="text-sm text-[#64748B] mb-8">
            {isRegister ? 'Regístrate para comenzar' : 'Ingresa tus credenciales'}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">
                  {t('login.nameLabel')}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    className="pl-10 h-11 bg-white border-[#E2E8F0] rounded-lg text-sm focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                    required
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">
                {t('login.emailLabel')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="pl-10 h-11 bg-white border-[#E2E8F0] rounded-lg text-sm focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">
                {t('login.passwordLabel')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11 bg-white border-[#E2E8F0] rounded-lg text-sm focus:border-[#0F172A] focus:ring-1 focus:ring-[#0F172A]"
                  required
                  minLength={isRegister ? 8 : 6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!isRegister && (
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-[#64748B] hover:text-[#F72585] transition-colors"
                  >
                    {t('login.forgot')}
                  </Link>
                </div>
              )}
            </div>

            <div className="flex justify-center">
              <TurnstileWidget
                ref={turnstileRef}
                action={isRegister ? 'register' : 'login'}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] rounded-lg font-medium text-sm transition-all"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isRegister ? 'Creando...' : 'Ingresando...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isRegister ? 'Crear cuenta' : 'Iniciar sesión'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-sm text-[#64748B] hover:text-[#0F172A] transition-colors"
            >
              {isRegister ? '¿Ya tienes cuenta? ' : '¿No tienes cuenta? '}
              <span className="text-[#F72585] font-medium">
                {isRegister ? 'Inicia sesión' : 'Regístrate'}
              </span>
            </button>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Right - Decorative */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-[#0F172A]">
        <img
          src="https://images.pexels.com/photos/15153700/pexels-photo-15153700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="CavernicolApp"
          className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/90 via-[#0F172A]/70 to-[#F72585]/20" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="max-w-md">
            <h2 className="text-4xl font-bold text-white tracking-tight mb-4">
              Gestión inteligente para tu negocio
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              CRM, agenda, IA conversacional, automatización y reportes. 
              Todo adaptado a tu industria.
            </p>
            <div className="flex flex-wrap gap-2 mt-8">
              {['Belleza', 'Salud', 'Fitness', 'Educación', 'Automotriz', 'Inmobiliario'].map((v) => (
                <span key={v} className="px-3 py-1 bg-white/10 text-white/80 text-xs rounded-full backdrop-blur-sm border border-white/10">
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
